import { useState, useEffect, useRef } from "react";
import { all_routes } from "../../../router/all_routes";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { API } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import { getAPIHeaders } from "../../../../common/Utils";
import PreLoader from "../../../../core/common/preLoader";
import AddSyllabus from "./syllabusAdd";
import ModifySyllabus from "./syllabusModify";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import { useAccess } from "../../../../hooks/accessContext";
import DeleteModal from "../../../../core/modals/deleteModal";
import CustomToast from "../../../../core/common/customToast";
import "../../../../style/css/shared.scss";

const Syllabus = () => {
  const { hasAcademicMgntCreate, hasAcademicMgntEdit, hasAcademicMgntDelete } =
    useAccess();
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>(searchParams.get('searchText') || '');
  const { isLoading, data, callFetch } = useFetch();
  const [syllabus, setSyllabus] = useState<any>([]);
  const [editId, setEditId] = useState<number>(0);
  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  });
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<"table" | "card">("table");
  const [uploadingSyllabus, setUploadingSyllabus] = useState<number | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState<boolean>(false);
  const [uploadConfirmationDetails, setUploadConfirmationDetails] = useState<{
    syllabusId: number | null;
    syllabusTitle: string;
  }>({
    syllabusId: null,
    syllabusTitle: "",
  });

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    setSearchText(searchParams.get('searchText') || '');
  }, [searchParams]);

  useEffect(() => {
    if (data) {
      setSyllabus(data);
    }
  }, [data]);

  const fetch = () => {
    callFetch(API.SYLLABUS_SEARCH, "GET", getAPIHeaders(), null, null);
  };

  const deleteData = (id: number) => {
    callFetch(`${API.SYLLABUS}/${id}`, "DELETE", getAPIHeaders(), null, null)
      .then(() => {
        fetch();
      })
      .catch((error) => {
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message:
            error?.response?.data ||
            "Something went wrong, please try again later.",
        }));
      });
  };

  const handleUploadClick = (syllabusId: number) => {
    setUploadingSyllabus(syllabusId);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !uploadingSyllabus) {
      return;
    }

    // Store the selected files temporarily
    setSelectedFiles(files);
    
    // Find the syllabus title for better user experience
    const currentSyllabus = syllabus.find((item: any) => item.id === uploadingSyllabus);
    const syllabusTitle = currentSyllabus?.title || "selected syllabus";
    
    // Set up confirmation details
    setUploadConfirmationDetails({
      syllabusId: uploadingSyllabus,
      syllabusTitle: syllabusTitle
    });
    
    // Show confirmation modal
    setShowUploadConfirmation(true);
  };
  
  const handleConfirmUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0 || !uploadConfirmationDetails.syllabusId) {
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append("files", selectedFiles[i]);
    }

    try {
      const endpoint = API.GENERATE_SYLLABUS.replace(
        ":syllabusId",
        uploadConfirmationDetails.syllabusId.toString()
      );
      const headers = {
        ...getAPIHeaders(),
        "Content-Type": "multipart/form-data",
      };

      const result = await callFetch(endpoint, "POST", headers, null, formData);

      setToastSettings({
        show: true,
        message: "Syllabus successfully generated from uploaded file.",
        heading: "Success",
        type: "SUCCESS",
      });

      // Refresh the syllabus list
      fetch();
    } catch (error: any) {
      setToastSettings({
        show: true,
        message:
          error?.response?.data ||
          "Failed to generate syllabus. Please try again.",
        heading: "Error",
        type: "ERROR",
      });
    } finally {
      // Clean up
      setUploadingSyllabus(null);
      setSelectedFiles(null);
      setShowUploadConfirmation(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const handleCancelUpload = () => {
    // Clean up
    setUploadingSyllabus(null);
    setSelectedFiles(null);
    setShowUploadConfirmation(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">{text}</h6>
      ),
      sorter: (a: any, b: any) => a.id - b.id,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Syllabus",
      dataIndex: "title",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
            <span className="text-primary fw-bold">
              {record.title.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-semibold syllabus-title">
              <Link
                to="#"
                className="text-reset text-decoration-none"
                onClick={() => {
                  setEditId(record?.id);
                }}
                data-bs-toggle="modal"
                data-bs-target="#edit_syllabus"
              >
                {record.title}
              </Link>
            </h6>
            <div className="d-flex align-items-center mt-1 syllabus-info">
              <span className="badge badge-soft-primary me-2">
                {record.code}
              </span>
              {record.description && (
                <span
                  className="fs-12 text-muted description-content text-truncate"
                  style={{ maxWidth: "250px" }}
                  title={record.description}
                >
                  {record.description}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title?.localeCompare(b.title),
      defaultSortOrder: "ascend",
      width: "62%",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-flex justify-content-center">
          <button
            className="me-2 btn btn-secondary btn-sm align-items-center d-flex"
            onClick={() => {
              navigation(`${all_routes.syllabusdetails}?id=${record?.id}`);
            }}
            title="Manage Syllabus Details"
          >
            <i className="ti ti-settings me-1"/>Manage Syllabus
          </button>
          <Link
            hidden={!hasAcademicMgntEdit()}
            to="#"
            className="me-2 icon-edit"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#edit_syllabus"
            onClick={() => {
              setEditId(record?.id);
            }}
            title="Edit Syllabus"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            hidden={!hasAcademicMgntEdit()}
            to="#"
            className="me-2 icon-upload"
            onClick={() => handleUploadClick(record.id)}
            title="Upload Syllabus Index"
          >
            <i className="ti ti-file-upload" />
          </Link>
          <Link
            hidden={!hasAcademicMgntDelete()}
            to="#"
            className="icon-delete"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() =>
              setDeleteItem({
                id: record.id,
                message: `Are you sure you want to delete ${record.title}? This can't be undone.`,
              })
            }
            title="Delete Syllabus"
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
      width: "30%",
      align: "right" as "right",
    },
  ];

  const renderAdd = () => {
    return <AddSyllabus onSuccess={fetch} />;
  };

  const renderEdit = () => {
    return (
      <ModifySyllabus
        readonly={!hasAcademicMgntEdit()}
        onSuccess={fetch}
        syllabus={syllabus?.find((el: any) => el.id === editId)}
      />
    );
  };

  // Upload Confirmation Modal component
  const UploadConfirmationModal = () => {
    return (
      <div className={`modal fade ${showUploadConfirmation ? 'show' : ''}`} id="upload_confirmation_modal" tabIndex={-1} 
           style={{ display: showUploadConfirmation ? 'block' : 'none' }}
           aria-labelledby="uploadConfirmationModalLabel" aria-hidden={!showUploadConfirmation}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="uploadConfirmationModalLabel">Confirm Upload</h5>
              <button type="button" className="btn-close" onClick={handleCancelUpload} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to upload and generate syllabus content for <strong>{uploadConfirmationDetails.syllabusTitle}</strong>?</p>
              {selectedFiles && (
                <div className="mt-3">
                  <h6>Selected Files:</h6>
                  <ul className="list-group">
                    {Array.from(selectedFiles).map((file, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <i className="ti ti-file me-2"></i>
                          {file.name}
                        </div>
                        <span className="badge bg-primary rounded-pill">{(file.size / 1024).toFixed(2)} KB</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="alert alert-info mt-3">
                <i className="ti ti-info-circle me-2"></i>
                This will generate syllabus content based on the uploaded file(s). The process may take a moment to complete.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCancelUpload}>Cancel</button>
              <button type="button" className="btn btn-primary ms-2" onClick={handleConfirmUpload}>
                <i className="ti ti-upload me-1"></i> Upload & Generate
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deleteData(deleteItem?.id)}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Upload Confirmation Modal */}
      <UploadConfirmationModal />
      {/* Hidden file input for upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Syllabus</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Academics</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    <Link to={all_routes.syllabus}>
                      Syllabus
                    </Link> 
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <button
                  disabled={!hasAcademicMgntCreate()}
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_syllabus"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Syllabus
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Syllabus List</h5>
              <div className="view-toggle">
                <button
                  className={`btn btn-sm me-2 ${
                    viewType === "table" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("table")}
                  aria-pressed={viewType === "table"}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1" aria-hidden="true"></i> Table
                  View
                </button>
                <button
                  className={`btn btn-sm ${
                    viewType === "card" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("card")}
                  aria-pressed={viewType === "card"}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1" aria-hidden="true"></i>{" "}
                  Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === "table" ? (
                <Table
                  dataSource={syllabus}
                  columns={columns}
                  Selection={false}
                  refresh={fetch}
                  defaultSearchText={searchText}
                />
              ) : (
                <div className="syllabus-cards p-3">
                  <div className="row">
                    {syllabus.map((syllabusItem: any) => (
                      <div
                        className="col-xl-4 col-lg-6 col-md-6 mb-3"
                        key={syllabusItem.id}
                      >
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {syllabusItem.title.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold syllabus-title">
                                  <Link
                                    to="#"
                                    className="text-reset text-decoration-none"
                                    onClick={() => {
                                      setEditId(syllabusItem?.id);
                                    }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_syllabus"
                                  >
                                    {syllabusItem.title}
                                  </Link>
                                </h6>
                              </div>
                              <span className="badge badge-soft-primary">
                                {syllabusItem.code}
                              </span>
                            </div>

                            {syllabusItem.description && (
                              <p className="fs-12 text-muted mb-3">
                                {syllabusItem.description.length > 100
                                  ? syllabusItem.description.substring(0, 100) +
                                    "..."
                                  : syllabusItem.description}
                              </p>
                            )}

                            {/* Syllabus content summary */}
                            {syllabusItem.items &&
                              syllabusItem.items.length > 0 && (
                                <div className="syllabus-content mb-3">
                                  <h6 className="fs-14 mb-2">
                                    <i className="ti ti-list me-1"></i> Content
                                    Items
                                  </h6>
                                  <div className="syllabus-items-list">
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="fs-12 fw-medium text-dark">
                                        Total Items
                                      </span>
                                      <span className="badge badge-soft-info">
                                        {syllabusItem.items.length}
                                      </span>
                                    </div>
                                    <div className="small content-preview">
                                      {syllabusItem.items
                                        .slice(0, 3)
                                        .map((item: any, idx: number) => (
                                          <div
                                            key={idx}
                                            className="d-flex align-items-center mb-1"
                                          >
                                            <i className="ti ti-point fs-10 me-1 text-primary"></i>
                                            <span
                                              className="text-truncate"
                                              style={{ maxWidth: "90%" }}
                                            >
                                              {item.title}
                                            </span>
                                          </div>
                                        ))}
                                      {syllabusItem.items.length > 3 && (
                                        <div className="text-primary fs-12 mt-1 text-end">
                                          +{syllabusItem.items.length - 3} more
                                          items...
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Subjects using this syllabus, if available */}
                            {syllabusItem.subjects &&
                              syllabusItem.subjects.length > 0 && (
                                <div className="subjects-info mb-3">
                                  <h6 className="fs-14 mb-2">
                                    <i className="ti ti-books me-1"></i> Used in
                                    Subjects
                                  </h6>
                                  <div className="d-flex flex-wrap">
                                    {syllabusItem.subjects
                                      .slice(0, 3)
                                      .map((subject: any, idx: number) => (
                                        <span
                                          key={idx}
                                          className="badge badge-soft-secondary me-1 mb-1"
                                        >
                                          {subject.title}
                                        </span>
                                      ))}
                                    {syllabusItem.subjects.length > 3 && (
                                      <span className="badge badge-soft-primary mb-1">
                                        +{syllabusItem.subjects.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                            <div className="d-flex flex-wrap gap-1 justify-content-between mt-3">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  navigation(
                                    `${all_routes.syllabusdetails}?id=${syllabusItem?.id}`
                                  );
                                }}
                                title="View Syllabus Details"
                              >
                                <i className="ti ti-eye me-1"></i> View Details
                              </button>

                              <div>
                                <Link
                                  hidden={!hasAcademicMgntEdit()}
                                  to="#"
                                  className="btn btn-sm btn-outline-primary me-2"
                                  data-bs-toggle="modal"
                                  data-inert={true}
                                  data-bs-target="#edit_syllabus"
                                  onClick={() => {
                                    setEditId(syllabusItem?.id);
                                  }}
                                  title="Edit Syllabus"
                                >
                                  <i className="ti ti-edit me-1" /> Edit
                                </Link>
                                <button
                                  hidden={!hasAcademicMgntEdit()}
                                  className="btn btn-sm btn-outline-info me-2"
                                  onClick={() => handleUploadClick(syllabusItem.id)}
                                  title="Upload Syllabus Index"
                                >
                                  <i className="ti ti-file-upload me-1" /> Upload
                                </button>
                                <Link
                                  hidden={!hasAcademicMgntDelete()}
                                  to="#"
                                  className="btn btn-sm btn-outline-danger"
                                  data-bs-toggle="modal"
                                  data-inert={true}
                                  data-bs-target="#delete_modal"
                                  onClick={() =>
                                    setDeleteItem({
                                      id: syllabusItem.id,
                                      message: `Are you sure you want to delete ${syllabusItem.title}? This can't be undone.`,
                                    })
                                  }
                                  title="Delete Syllabus"
                                >
                                  <i className="ti ti-trash me-1" /> Delete
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Empty state when no syllabus items are available */}
                    {syllabus.length === 0 && !isLoading && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-file-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No syllabus found</h5>
                          <p className="text-muted">
                            No syllabus items are available in the system.
                          </p>
                          <button
                            className="btn btn-primary mt-2"
                            onClick={() =>
                              (
                                document.querySelector(
                                  '[data-bs-target="#add_syllabus"]'
                                ) as HTMLButtonElement
                              )?.click()
                            }
                            disabled={!hasAcademicMgntCreate()}
                          >
                            <i className="ti ti-circle-plus me-2"></i>Add
                            Syllabus
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}
      {renderAdd()}
      {renderEdit()}
    </>
  );
};

export default Syllabus;
